import { Alert, Loader, Modal, typeBase14RegularProperties } from '@traceair/tds';
import { useState } from 'react';
import { useAppStore } from '../../../../store/appStore';
import { openCloseAnimationDuration } from '@traceair/tds/src/modal/constants';
import { DateChangeIcon } from '../../../../images/components/DateChangeIcon';
import { stylesheet } from 'typestyle';
import { DateChangeMissionCalendar } from './DateChangeMissionCalendar';
import { DateChangeReason } from './DateChangeReason';
import { Dayjs } from 'dayjs';
import { useGetAvailableDates } from './useGetAvailableDates';
import { useRescheduleFlight } from './useRescheduleFlight';
import { RescheduleReason } from '../../../../consts/RescheduleReason';

const css = stylesheet({
  loader: {
    height: 222,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    maxWidth: 420,
  },
  note: {
    ...typeBase14RegularProperties,
    marginBottom: 24,
  },
  frequency: {
    textTransform: 'capitalize',
  },
});

export const DateChangeMission = () => {
  const [isOpen, setIsOpen] = useState(true);
  const { selectedDateChangeMission, setSelectedDateChangeMission } = useAppStore();

  const [newDate, setNewDate] = useState<Dayjs | undefined>(undefined);
  const [reason, setReason] = useState<string | undefined>(selectedDateChangeMission?.params?.initialReason);
  const [note, setNote] = useState<string>('');

  const site = selectedDateChangeMission?.mission.site;
  const schedule = selectedDateChangeMission?.mission.schedule;

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setSelectedDateChangeMission(null);
    }, openCloseAnimationDuration);
  };

  const { availableGreenDays, availableDaysError } = useGetAvailableDates();
  const { isSubmittingRescheduleRequest, rescheduleFlight } = useRescheduleFlight(handleClose, newDate, reason, note);

  if (!site) return null;

  const isLoading = availableGreenDays === null;
  const isNoteRequired = [RescheduleReason.EquipmentIssue, RescheduleReason.Other].includes(reason as RescheduleReason);
  const isValid = Boolean(newDate && reason && (!isNoteRequired || note.trim()));

  const siteDescription = (
    <span>
      {site.name}
      {schedule && !!schedule.flightFrequency && <span className={css.frequency}> ∙ {schedule.flightFrequency}</span>}
    </span>
  );

  return (
    <>
      <Modal
        className={css.container}
        title="Change mission date"
        description={siteDescription}
        isOpen={isOpen}
        headerIconViewProps={{ icon: DateChangeIcon }}
        cancelButtonProps={{
          text: 'Cancel',
          onClick: handleClose,
        }}
        closableByOverlayClick
        okButtonProps={{
          text: 'Reschedule flight',
          onClick: rescheduleFlight,
          loading: isSubmittingRescheduleRequest,
          disabled: isLoading || !isValid,
        }}
        onClose={handleClose}
      >
        <div className={css.note}>
          Changing the date will reschedule the entire mission scope. Flight rescheduling within the allowed range is
          automatically approved.
        </div>

        {availableDaysError ? (
          <Alert type="warning" title={availableDaysError} />
        ) : (
          <>
            {isLoading ? (
              <div className={css.loader}>
                <Loader />
              </div>
            ) : (
              <div>
                <DateChangeMissionCalendar
                  schedule={schedule}
                  newDate={newDate}
                  availableGreenDays={availableGreenDays}
                  setNewDate={setNewDate}
                />

                <DateChangeReason
                  reason={reason}
                  setReason={setReason}
                  note={note}
                  setNote={setNote}
                  isNoteRequired={isNoteRequired}
                />
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};
